import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import type { Gate, GateType } from '../api/generated';

/**
 * The tenant's gates (SAM-946): GET/POST/PUT /crud/gate and GET /crud/gate_type. The gate list is small (a
 * community has a handful of entrances), so one page of 500 is the whole list.
 */
const GATES_KEY = ['gates'] as const;
const LIST_LIMIT = 500;

type Page<T> = { data?: T[] } | T[];
const rowsOf = <T,>(res: unknown): T[] => {
  const page = res as Page<T> | undefined;
  return Array.isArray(page) ? page : page?.data ?? [];
};

export const useGates = () =>
  useQuery({
    queryKey: GATES_KEY,
    queryFn: async () =>
      rowsOf<Gate>(await defaultInstance({ url: '/gate', method: 'GET', params: { limit: LIST_LIMIT, page: 1 } })),
  });

export const useGateTypes = () =>
  useQuery({
    queryKey: ['gate-types'],
    queryFn: async () =>
      rowsOf<GateType>(await defaultInstance({ url: '/gate_type', method: 'GET', params: { limit: LIST_LIMIT, page: 1 } })),
    staleTime: 10 * 60 * 1000,
  });

export const useCreateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Gate>) => defaultInstance({ url: '/gate', method: 'POST', data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GATES_KEY }),
  });
};

export const useUpdateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Gate> }) =>
      defaultInstance({ url: `/gate/${id}`, method: 'PUT', data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GATES_KEY }),
  });
};
